import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Warning, ChevronLeft, ChevronRight, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getSearchHistory, clearSearchHistory } from '../../services/searchAdmin';

const SearchHistory = () => {
  const { t } = useTranslation();
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false });

  // Fetch search history with pagination
  const fetchSearchHistory = useCallback(async (pageArg, rowsArg) => {
    setLoading(true);
    try {
      const response = await getSearchHistory({
        page: pageArg + 1, // backend expects 1-based
        page_size: rowsArg,
        ordering: '-created_at',
      });
      
      setSearchHistory(response.results || []);
      setTotalCount(response.count || 0);
      setTotalPages(response.total_pages || 1);
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSearchHistory(page, rowsPerPage);
  }, [page, rowsPerPage, fetchSearchHistory]);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Refresh function
  const handleRefresh = () => {
    setPage(0);
    fetchSearchHistory(0, rowsPerPage);
  };

  // Clear all search history
  const handleClearHistory = async () => {
    try {
      await clearSearchHistory();
      setSnackbar({ open: true, message: t('searchHistory:messages.clearedSuccess'), severity: 'success' });
      fetchSearchHistory(page, rowsPerPage);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleDeleteClick = () => {
    setDeleteModal({ open: true });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false });
  };

  const handleDeleteConfirm = () => {
    handleClearHistory();
    setDeleteModal({ open: false });
  };

  // Get query type color
  const getQueryTypeColor = (type) => {
    switch (type) {
      case 'keyword': return '#2196F3';
      case 'url': return '#4CAF50';
      case 'offer_id': return '#FF9800';
      case 'image': return '#9C27B0';
      default: return '#757575';
    }
  };

  // Get translation service color
  const getServiceColor = (service) => {
    switch (service) {
      case 'google': return '#4285F4';
      case 'baidu': return '#2932E1';
      default: return '#757575';
    }
  };

  // Add serial numbers to search history for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const getColumns = () => [
    { field: 'sn', headerName: t('searchHistory:table.sn'), width: 80, sortable: false, filterable: false },
    
    { 
      field: 'user_username', 
      headerName: t('searchHistory:table.user'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const username = params.row?.user_username;
        return <span>{username && username.trim() !== '' ? username : t('searchHistory:anonymous')}</span>;
      }
    },
    
    { 
      field: 'query', 
      headerName: t('searchHistory:table.query'), 
      flex: 2, 
      minWidth: 200,
      renderCell: (params) => {
        const query = params.row?.query;
        return (
          <Tooltip title={query} placement="top">
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}>
              {query && query.length > 50 ? `${query.substring(0, 50)}...` : query}
            </span>
          </Tooltip>
        );
      }
    },
    
    { 
      field: 'query_type', 
      headerName: t('searchHistory:table.type'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const type = params.row?.query_type || 'unknown';
        const typeKey = `searchHistory:queryTypes.${type}`;
        const label = t(typeKey, { defaultValue: type.charAt(0).toUpperCase() + type.slice(1) });
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: getQueryTypeColor(type),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    
    { 
      field: 'translated_query', 
      headerName: t('searchHistory:table.translated'), 
      flex: 2, 
      minWidth: 200,
      renderCell: (params) => {
        const translated = params.row?.translated_query;
        if (!translated || translated === params.row?.query) {
          return <span style={{ color: '#999' }}>{t('searchHistory:notTranslated')}</span>;
        }
        return (
          <Tooltip title={translated} placement="top">
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}>
              {translated.length > 50 ? `${translated.substring(0, 50)}...` : translated}
            </span>
          </Tooltip>
        );
      }
    },
    
    { 
      field: 'offer_id', 
      headerName: t('searchHistory:table.offerId'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const offerId = params.row?.offer_id;
        return <span>{offerId && offerId.trim() !== '' ? offerId : 'N/A'}</span>;
      }
    },
    
    { 
      field: 'translation_service', 
      headerName: t('searchHistory:table.service'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const service = params.row?.translation_service;
        if (!service) {
          return <span style={{ color: '#999' }}>{t('searchHistory:none')}</span>;
        }
        return (
          <Chip
            label={service.charAt(0).toUpperCase() + service.slice(1)}
            size="small"
            sx={{
              backgroundColor: getServiceColor(service),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    
    { 
      field: 'search_results_count', 
      headerName: t('searchHistory:table.results'), 
      flex: 1, 
      minWidth: 80,
      renderCell: (params) => {
        const count = params.row?.search_results_count;
        return <span>{count !== null && count !== undefined ? count : 'N/A'}</span>;
      }
    },
    
    { 
      field: 'created_at', 
      headerName: t('searchHistory:table.date'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const createdAt = params.row?.created_at;
        if (!createdAt) return <span>N/A</span>;
        
        try {
          const date = new Date(createdAt);
          if (isNaN(date.getTime())) {
            return <span>N/A</span>;
          }
          return <span>{date.toLocaleDateString()}</span>;
        } catch (error) {
          console.error('Error formatting date:', error, createdAt);
          return <span>N/A</span>;
        }
      }
    }
  ];

  if (loading && searchHistory.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('searchHistory:loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('searchHistory:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('searchHistory:refresh')}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteClick}
            disabled={loading || searchHistory.length === 0}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
          >
            {t('searchHistory:clearHistory')}
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ height: 520, width: '100%', background: 'white', borderRadius: 2, boxShadow: 1 }}>
        {searchHistory.length === 0 && !loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
              {t('searchHistory:noHistory')}
            </Typography>
            <Button variant="outlined" onClick={handleRefresh}>
              {t('searchHistory:refreshData')}
            </Button>
          </Box>
        ) : (
          <DataGrid
            rows={addSerialNumbers(searchHistory, page, rowsPerPage)}
            columns={getColumns()}
            pageSize={rowsPerPage}
            rowsPerPageOptions={[]}
            disableSelectionOnClick
            loading={loading}
            hideFooter
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme => theme.palette.primary.main,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem'
              },
              '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                color: 'white'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#FFF0F3'
              },
              '& .MuiDataGrid-cell': {
                fontSize: '1rem'
              }
            }}
          />
        )}
      </Box>

      {/* Custom Pagination Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="body2" color="textSecondary">
          {t('searchHistory:pagination.showing')} {searchHistory.length} {t('searchHistory:pagination.of')} {totalCount} {t('searchHistory:pagination.records')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('searchHistory:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('searchHistory:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
          <Typography variant="body2" sx={{ ml: 2 }}>
            {t('searchHistory:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <select
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100px'
              }}
            >
              {[10, 20, 50, 100].map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
              <option value="custom">Custom</option>
            </select>
          ) : (
            <input
              type="text"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100px',
                textAlign: 'center'
              }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteModal.open} 
        onClose={handleDeleteCancel}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          color: '#DC143C', 
          fontWeight: 'bold',
          pb: 1
        }}>
          <Warning sx={{ color: '#DC143C', fontSize: 28 }} />
          {t('searchHistory:delete.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
            {t('searchHistory:delete.message')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ 
            backgroundColor: '#FFF3E0', 
            p: 2, 
            borderRadius: 1, 
            border: '1px solid #FFB74D',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Warning sx={{ color: '#F57C00', fontSize: 20 }} />
            {t('searchHistory:delete.warning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ 
              borderColor: '#666', 
              color: '#666',
              '&:hover': { 
                borderColor: '#333', 
                backgroundColor: '#f5f5f5' 
              }
            }}
          >
            {t('searchHistory:delete.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' },
              px: 3
            }}
            startIcon={<Delete />}
          >
            {t('searchHistory:delete.clearHistory')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchHistory;